import React from 'react';
import styled from 'styled-components';
import { Download, Smartphone, Shield, Zap, Database, Network } from 'lucide-react';

const PageContainer = styled.div`
    width: 100%;
    height: auto;
    padding: 20px 0px;
    color: white;
    font-family: Very Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const GradientOrb1 = styled.div`
    position: absolute;
    top: -20%;
    left: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
`;

const GradientOrb2 = styled.div`
    position: absolute;
    bottom: -20%;
    right: -10%;
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%);
    pointer-events: none;
`;

const ContentWrapper = styled.div`
    position: relative;
    z-index: 2;
    width: 80%;
    max-width: 1200px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    
    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 2rem;
        width: 90%;
    }
`;

const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
`;

const Title = styled.h1`
    font-family: Very Sans Headline, Sans Serif;
    font-size: 48px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
    line-height: 1.2;
    
    .highlight {
        background: linear-gradient(135deg, #3B82F6 0%, #a78bfa 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
`;

const Subtitle = styled.p`
    color: #94A3B8;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
    margin: 0;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
`;

const DownloadButton = styled.button`
    background: #3B82F6;
    color: white;
    border: none;
    padding: 14px 28px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: Very Sans, Sans Serif;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
    
    &:hover {
        background: #2563EB;
        transform: translateY(-2px);
        box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
    }
`;

const StatsRow = styled.div`
    display: flex;
    gap: 2rem;
    padding-top: 1rem;
`;

const StatItem = styled.div``;

const StatValue = styled.div`
    font-family: Very Sans Bold, Sans Serif;
    font-size: 28px;
    color: #ffffff;
    margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
    color: #94A3B8;
    font-size: 12px;
    font-family: Very Sans, Sans Serif;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const RightSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
`;

const FeatureCard = styled.div`
    background: rgba(255, 255, 255, 0.025);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    padding: 1.5rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(59, 130, 246, 0.3);
        transform: translateY(-2px);
    }
`;

const IconWrapper = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(59, 130, 246, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
    font-family: Very Sans Bold, Sans Serif;
    font-size: 16px;
    color: #ffffff;
    margin: 0 0 0.5rem 0;
`;

const FeatureDescription = styled.p`
    color: #94A3B8;
    font-size: 13px;
    font-family: Very Sans, Sans Serif;
    margin: 0;
    line-height: 1.5;
`;

const RequirementsBox = styled.div`
    background: rgba(255, 255, 255, 0.025);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    padding: 1.5rem;
`;

const RequirementsTitle = styled.h4`
    font-family: Very Sans Bold, Sans Serif;
    font-size: 14px;
    color: #ffffff;
    margin: 0 0 1rem 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const RequirementsList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const RequirementItem = styled.li`
    color: #94A3B8;
    font-size: 13px;
    font-family: Very Sans, Sans Serif;
    padding-left: 1.5rem;
    position: relative;
    
    &:before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #3B82F6;
        font-weight: bold;
    }
`;

const Version = styled.div`
    color: #94A3B8;
    font-size: 12px;
    margin-top: 1rem;
`;

const HydrixNode = () => {
    const handleDownload = () => {
        const downloadUrl = 'https://chainfree.info:5010/download/hydrix-node.apk';
        
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'hydrix-node.apk';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('🚀 Hydrix Node APK download initiated');
    };

    const features = [
        {
            icon: <Smartphone size={22} color="#3B82F6" />,
            title: 'Mobile-First',
            description: 'Run a full node on Android'
        },
        {
            icon: <Shield size={22} color="#3B82F6" />,
            title: 'Secure',
            description: 'Full control of your node'
        },
        {
            icon: <Zap size={22} color="#3B82F6" />,
            title: 'Efficient',
            description: 'Minimal battery usage'
        },
        {
            icon: <Database size={22} color="#3B82F6" />,
            title: 'Full Node',
            description: 'Complete blockchain storage'
        }
    ];

    return (
        <PageContainer>
            <GradientOrb1 />
            <GradientOrb2 />
            <ContentWrapper>
                <LeftSection>
                    <Title>
                        Run a <span className="highlight">Blockchain Node</span> on Your Phone
                    </Title>
                    <Subtitle>
                        Be part of the Hydrix network infrastructure. Run a full node, 
                        earn rewards, and contribute to true decentralization—all from your Android device.
                    </Subtitle>
                    <ButtonGroup>
                        <DownloadButton onClick={handleDownload}>
                            <Download size={18} />
                            Download for Android
                        </DownloadButton>
                    </ButtonGroup>
                    <Version>Version 1.0.0 • Android 8.0+</Version>
                    
                    <StatsRow>
                        <StatItem>
                            <StatValue>500+</StatValue>
                            <StatLabel>Active Nodes</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>24/7</StatValue>
                            <StatLabel>Uptime</StatLabel>
                        </StatItem>
                        <StatItem>
                            <StatValue>1.2M+</StatValue>
                            <StatLabel>Blocks</StatLabel>
                        </StatItem>
                    </StatsRow>
                </LeftSection>

                <RightSection>
                    <FeaturesGrid>
                        {features.map((feature, index) => (
                            <FeatureCard key={index}>
                                <IconWrapper>
                                    {feature.icon}
                                </IconWrapper>
                                <FeatureTitle>{feature.title}</FeatureTitle>
                                <FeatureDescription>{feature.description}</FeatureDescription>
                            </FeatureCard>
                        ))}
                    </FeaturesGrid>
                    
                    <RequirementsBox>
                        <RequirementsTitle>Requirements</RequirementsTitle>
                        <RequirementsList>
                            <RequirementItem>Android 8.0+</RequirementItem>
                            <RequirementItem>4GB RAM minimum</RequirementItem>
                            <RequirementItem>10GB free storage</RequirementItem>
                            <RequirementItem>Stable internet</RequirementItem>
                        </RequirementsList>
                    </RequirementsBox>
                </RightSection>
            </ContentWrapper>
        </PageContainer>
    );
};

export default HydrixNode;